import { useRef, useState, useCallback, useEffect } from 'react';

interface Text3DHoverProps {
  children: React.ReactNode;
  variant?: 'lift' | 'glow';
  className?: string;
}

export default function Text3DHover({ children, variant = 'lift', className = '' }: Text3DHoverProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rawX = (e.clientX - rect.left - cx) / cx;
    const rawY = (e.clientY - rect.top - cy) / cy;
    targetRef.current = { x: rawX, y: rawY };
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    const lerp = () => {
      const t = targetRef.current;
      const c = currentRef.current;
      const factor = 0.15;
      c.x += (t.x - c.x) * factor;
      c.y += (t.y - c.y) * factor;
      if (Math.abs(t.x - c.x) < 0.005 && Math.abs(t.y - c.y) < 0.005) {
        c.x = t.x;
        c.y = t.y;
      }

      if (variant === 'lift') {
        setTransform(`translateY(-3px) rotateX(${c.y * -2}deg) rotateY(${c.x * 3}deg)`);
      } else {
        setTransform(`translateY(-2px) rotateX(${c.y * -1.5}deg) rotateY(${c.x * 2}deg) scale(1.02)`);
      }

      rafRef.current = requestAnimationFrame(lerp);
    };
    rafRef.current = requestAnimationFrame(lerp);
  }, [variant]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    targetRef.current = { x: 0, y: 0 };

    const settle = () => {
      const c = currentRef.current;
      c.x += (0 - c.x) * 0.1;
      c.y += (0 - c.y) * 0.1;

      if (Math.abs(c.x) < 0.005 && Math.abs(c.y) < 0.005) {
        c.x = 0;
        c.y = 0;
        setTransform('');
        return;
      }

      if (variant === 'lift') {
        setTransform(`translateY(0px) rotateX(${c.y * -2}deg) rotateY(${c.x * 3}deg)`);
      } else {
        setTransform(`translateY(0px) rotateX(${c.y * -1.5}deg) rotateY(${c.x * 2}deg) scale(1)`);
      }
      rafRef.current = requestAnimationFrame(settle);
    };
    rafRef.current = requestAnimationFrame(settle);
  }, [variant]);

  const liftClasses = variant === 'lift'
    ? 'text-3d-lift'
    : 'text-3d-glow';

  const glowStyles = variant === 'glow' && isHovered
    ? {
        boxShadow: `0 0 40px 8px oklch(var(--accent-500) / 0.12), 0 0 80px 16px oklch(var(--primary-500) / 0.06)`,
        background: 'linear-gradient(135deg, oklch(var(--background-100) / 0.9), oklch(var(--background-50) / 0.5))',
      }
    : {};

  return (
    <div
      ref={containerRef}
      className={`${liftClasses} ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="text-3d-inner"
        style={{
          transform,
          transition: 'box-shadow 700ms cubic-bezier(0.25, 0.1, 0.25, 1), background 700ms cubic-bezier(0.25, 0.1, 0.25, 1)',
          ...glowStyles,
        }}
      >
        {children}
      </div>
    </div>
  );
}